import { isValidPhoneNumber } from 'libphonenumber-js'
import * as yup from 'yup'

import { TPriorSubmission } from '~/features/chatbotV2/models'
import {
  DEFAULT_PRE_CHAT_FORM_FIELD_MAPPINGS,
  PRE_CHAT_FORM_FIELD_LABELS,
} from '~/features/preChatForms/constants'
import {
  EFormDisplayRule,
  EFormFieldDataType,
  EFormFieldMapping,
  EPreChatFormFieldKeys,
  EPreChatFormKeys,
  TGuestPreChatForm,
  TGuestPreChatFormField,
} from '~/features/preChatForms/models'

type TPreChatFormFieldValues = Record<EFormFieldMapping, string>

export const createEmptyPreChatFormFieldValues = (): TPreChatFormFieldValues =>
  Object.fromEntries(
    DEFAULT_PRE_CHAT_FORM_FIELD_MAPPINGS.map((mapping) => [mapping, '']),
  ) as TPreChatFormFieldValues

const emptyStringToUndefined = (value: unknown) =>
  typeof value === 'string' && value.trim() === '' ? undefined : value

const getPreChatFormFieldValidationLabel = (field: TGuestPreChatFormField) =>
  PRE_CHAT_FORM_FIELD_LABELS[field[EPreChatFormFieldKeys.fieldMapping]] ??
  field[EPreChatFormFieldKeys.displayName]

const getPhoneValidation = (label: string, required: boolean) => {
  let schema = yup
    .string()
    .transform(emptyStringToUndefined)
    .label(label)
    .test('valid-phone', 'You must enter a valid phone number.', (value) => {
      if (!value) {
        return true
      }

      return isValidPhoneNumber(value.trim(), 'US')
    })

  if (required) {
    schema = schema.required()
  }

  return schema
}

const getFieldValidation = (field: TGuestPreChatFormField) => {
  const isRequired = field[EPreChatFormFieldKeys.required]
  const label = getPreChatFormFieldValidationLabel(field)

  switch (field[EPreChatFormFieldKeys.dataType]) {
    case EFormFieldDataType.email:
      return isRequired
        ? yup
            .string()
            .transform(emptyStringToUndefined)
            .label(label)
            .required()
            .email('Enter a valid email')
        : yup
            .string()
            .transform(emptyStringToUndefined)
            .label(label)
            .optional()
            .email('Enter a valid email')
    case EFormFieldDataType.phone:
      return getPhoneValidation(label, isRequired)
    case EFormFieldDataType.string:
    default:
      return isRequired
        ? yup.string().trim().label(label).required()
        : yup.string().label(label).optional()
  }
}

export const buildPreChatFormValidationSchema = (
  fields: TGuestPreChatFormField[],
) =>
  yup.object(
    fields.reduce<Record<string, yup.StringSchema>>(
      (schema, field) => ({
        ...schema,
        [field[EPreChatFormFieldKeys.fieldMapping]]: getFieldValidation(field),
      }),
      {},
    ),
  )

export const getPreChatFormInitialValues = (
  form: TGuestPreChatForm,
  priorSubmission?: TPriorSubmission | null,
): TPreChatFormFieldValues => {
  const values = createEmptyPreChatFormFieldValues()
  const shouldPrefill =
    form[EPreChatFormKeys.displayRule] ===
      EFormDisplayRule.everyConversationPrefilled && !!priorSubmission?.values

  form[EPreChatFormKeys.fields].forEach((field) => {
    const fieldMapping = field[EPreChatFormFieldKeys.fieldMapping]
    const fieldId = field[EPreChatFormFieldKeys.id]
    const prefilledValue = priorSubmission?.values[fieldId]

    if (shouldPrefill && prefilledValue != null) {
      values[fieldMapping] = String(prefilledValue)
    }
  })

  return values
}

export const mapPreChatFormValuesToSubmissionIds = (
  sortedFields: TGuestPreChatFormField[],
  values: TPreChatFormFieldValues,
): Record<string, string> =>
  sortedFields.reduce<Record<string, string>>((submissionValues, field) => {
    const fieldMapping = field[EPreChatFormFieldKeys.fieldMapping]
    const fieldId = field[EPreChatFormFieldKeys.id]

    return {
      ...submissionValues,
      [fieldId]: values[fieldMapping] ?? '',
    }
  }, {})
